import { useState, useEffect } from "react";

let activeWHIPConnection = null;

// Function to wait for ICE gathering to complete
async function waitToCompleteICEGathering(peerConnection) {
	return new Promise((resolve) => {
		setTimeout(function () {
			if (peerConnection.connectionState === "closed") {
				console.error(
					"Peer connection is closed, cannot access localDescription."
				);
				resolve(null);
				return;
			}
			resolve(peerConnection.localDescription);
		}, 1000);
		peerConnection.onicegatheringstatechange = () =>
			peerConnection.iceGatheringState === "complete" &&
			resolve(peerConnection.localDescription);
	});
}

// Function to post the SDP offer
async function postSDPOffer(endpoint, data) {
	return await fetch(endpoint, {
		method: "POST",
		mode: "cors",
		headers: {
			"content-type": "application/sdp",
		},
		body: data,
	});
}

// Function to negotiate the connection with the client offer
async function negotiateConnectionWithClientOffer(
	peerConnection,
	endpoint,
	setIsPublisherConnected
) {
	if (!endpoint || endpoint === "null") {
		console.error("Invalid endpoint provided.");
		return;
	}

	const offer = await peerConnection.createOffer();

	await peerConnection.setLocalDescription(offer);

	const ofr = await waitToCompleteICEGathering(peerConnection);
	if (!ofr) {
		throw Error("failed to gather ICE candidates for offer");
	}

	peerConnection.addEventListener("connectionstatechange", () => {
		if (peerConnection.connectionState === "connected") {
			setIsPublisherConnected(true);
		}

		if (peerConnection.connectionState === "closed") {
			setIsPublisherConnected(false);
		}
	});

	while (peerConnection.connectionState !== "closed") {
		const response = await postSDPOffer(endpoint, ofr.sdp);
		if (response.status === 201) {
			const answerSDP = await response.text();
			await peerConnection.setRemoteDescription(
				new RTCSessionDescription({ type: "answer", sdp: answerSDP })
			);
			return response.headers.get("Location");
		} else if (response.status === 405) {
			console.error("Update the URL passed into the WHIP or WHEP client");
		} else {
			// Handle error
		}

		await new Promise((r) => setTimeout(r, 3000));
	}
}

const useWHIPClient = ({ endpoint, setIsPublisherConnected }) => {
	const [peerConnection, setPeerConnection] = useState(null);
	const [localStream, setLocalStream] = useState(null);

	useEffect(() => {
		if (
			typeof window !== "undefined" &&
			typeof window.RTCPeerConnection === "undefined"
		) {
			console.error(
				"RTCPeerConnection is not available in this browser environment"
			);
			return;
		}

		if (activeWHIPConnection) {
			console.log(
				"Closing existing WHIP connection before creating a new one"
			);
			activeWHIPConnection.close();
			activeWHIPConnection = null;
		}

		try {
			const pc = new RTCPeerConnection({
				iceServers: [
					{
						urls: "stun:stun.cloudflare.com:3478",
					},
				],
				bundlePolicy: "max-bundle",
			});

			activeWHIPConnection = pc;
			setPeerConnection(pc);
		} catch (error) {
			console.error("Error creating RTCPeerConnection:", error);
			activeWHIPConnection = null;
		}

		return () => {
			if (peerConnection) {
				peerConnection.close();

				if (activeWHIPConnection === peerConnection) {
					activeWHIPConnection = null;
				}
			}
			if (localStream) {
				localStream.getTracks().forEach((track) => track.stop());
			}
		};
	}, []);

	useEffect(() => {
		if (!localStream || !endpoint || !peerConnection) return;

		const handleNegotiateConnection = async () => {
			await negotiateConnectionWithClientOffer(
				peerConnection,
				endpoint,
				setIsPublisherConnected
			);
		};

		peerConnection.addEventListener(
			"negotiationneeded",
			handleNegotiateConnection
		);

		const accessLocalMediaSources = async () => {
			localStream.getTracks().forEach((track) => {
				const existingTransceiver = peerConnection
					.getTransceivers()
					.find(
						(transceiver) =>
							transceiver.receiver.track.kind === track.kind
					);

				if (existingTransceiver) {
					existingTransceiver.sender.replaceTrack(track);
				} else {
					const transceiver = peerConnection.addTransceiver(track, {
						direction: "sendonly",
					});
					if (track.kind === "video" && transceiver.sender.track) {
						transceiver.sender.track.applyConstraints({
							width: 1280,
							height: 720,
						});
					}
				}
			});
		};

		accessLocalMediaSources().catch(console.error);

		return () => {
			peerConnection.removeEventListener(
				"negotiationneeded",
				handleNegotiateConnection
			);
		};
	}, [peerConnection, endpoint, localStream]);

	const disconnectPublisher = () => {
		if (peerConnection) {
			peerConnection.close();
			setPeerConnection(null);

			if (activeWHIPConnection === peerConnection) {
				activeWHIPConnection = null;
			}
		}

		if (localStream) {
			localStream.getTracks().forEach((track) => track.stop());
			setLocalStream(null);
		}
	};

	const muteStream = () => {
		localStream
			?.getAudioTracks()
			.forEach((track) => (track.enabled = false));
	};

	const unMuteStream = () => {
		localStream
			?.getAudioTracks()
			.forEach((track) => (track.enabled = true));
	};

	const videoOff = () => {
		localStream
			?.getVideoTracks()
			.forEach((track) => (track.enabled = false));
	};

	const videoOn = () => {
		localStream
			?.getVideoTracks()
			.forEach((track) => (track.enabled = true));
	};

	return {
		disconnectPublisher,
		muteStream,
		unMuteStream,
		videoOff,
		videoOn,
		setLocalStream,
		localStream,
	};
};

export default useWHIPClient;
